import { useEffect, useState } from 'react'
import * as Sentry from '@sentry/react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Spin, Result, Button } from 'antd'
import { IntegrationService } from '../services/integrations/integrationService'
import { useIntegrationContext } from '../context/integration'
import {
  ProcessingContainer,
  LoadingIcon,
  ResultContainer,
  SuccessIcon,
} from './styles/TrelloCallbackPageStyles'

export function TrelloCallbackPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { reloadIntegrations } = useIntegrationContext()
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  useEffect(() => {
    const handleCallback = async () => {
      try {
        // Extract token from URL fragment (Trello returns it after #)
        const hash = window.location.hash.substring(1) // Remove # prefix
        const hashParams = new URLSearchParams(hash)

        // Extract state from query params (if present) or hash
        const stateParam = searchParams.get('state') ?? hashParams.get('state')

        // Extract token from hash
        const token =
          hashParams.get('token') ?? hashParams.get('access_token') ?? hashParams.get('oauth_token')

        if (!token) {
          // Check for error in hash
          const error = hashParams.get('error') ?? hashParams.get('error_description')
          if (error) {
            setStatus('error')
            setErrorMessage(error || 'Authorization failed')
            return
          }

          setStatus('error')
          setErrorMessage('No token received from Trello. Please try again.')
          return
        }

        // Decode state to get user info (though we'll use auth headers)
        // Note: We don't actually need userId from state since we use auth headers
        // This is just for reference
        if (stateParam) {
          try {
            // Browser-compatible base64 decode
            atob(stateParam)
            // State parsing successful, but we use auth headers instead
          } catch {
            // State parsing failed, that's okay - we'll use auth
          }
        }

        // Send token to backend to save
        const result = await IntegrationService.connect('trello', {
          apiToken: token,
        })

        if (result.error) {
          setStatus('error')
          setErrorMessage(result.error.message || 'Failed to save Trello integration')
          return
        }

        // Success!
        setStatus('success')
        await reloadIntegrations()

        // Redirect to settings after a short delay
        setTimeout(() => {
          void navigate('/settings?section=integrations', { replace: true })
        }, 2000)
      } catch (error) {
        Sentry.captureException(error, {
          tags: { feature: 'integration', action: 'trello_callback' },
        })
        setStatus('error')
        setErrorMessage(error instanceof Error ? error.message : 'An unexpected error occurred')
      }
    }

    void handleCallback()
  }, [navigate, searchParams, reloadIntegrations])

  if (status === 'processing') {
    return (
      <ProcessingContainer>
        <Spin indicator={<LoadingIcon spin />} size="large" />
        <div>
          <h3>Connecting your Trello account...</h3>
          <p>Please wait while we save your integration.</p>
        </div>
      </ProcessingContainer>
    )
  }

  if (status === 'error') {
    return (
      <ResultContainer>
        <Result
          status="error"
          title="Connection Failed"
          subTitle={errorMessage ?? 'Failed to connect Trello account'}
          extra={[
            <Button
              type="primary"
              key="retry"
              onClick={() => navigate('/settings?section=integrations')}
            >
              Go to Settings
            </Button>,
          ]}
        />
      </ResultContainer>
    )
  }

  return (
    <ResultContainer>
      <Result
        icon={<SuccessIcon />}
        status="success"
        title="Trello Connected Successfully!"
        subTitle="Your Trello account has been connected. Redirecting to settings..."
        extra={[
          <Button
            type="primary"
            key="settings"
            onClick={() => navigate('/settings?section=integrations')}
          >
            Go to Settings
          </Button>,
        ]}
      />
    </ResultContainer>
  )
}
